import React, { useEffect, useState, useMemo, useRef } from 'react';
import ForceGraph2D, { ForceGraphMethods } from 'react-force-graph-2d';
import { forceRadial } from 'd3-force';
import { ideasService } from '../../services/ideas';

import { Idea } from '../../types/idea-machine';
import { useDarkMode } from '../../context/DarkModeContext';
import { Loader2, Maximize2, Minimize2, RefreshCw, Layers } from 'lucide-react';
import { Slider } from '../ui/slider';

interface GraphNode {
  id: string;
  group: 'seed' | 'idea';
  val: number; // size
  name: string;
  color?: string;
  category?: string;
  isSimilarity?: boolean;
  isSun?: boolean;
  // Physics properties
  fx?: number;
  fy?: number;
  x?: number;
  y?: number;
  vx?: number;
  vy?: number;
}

interface GraphLink {
  source: string;
  target: string;
  color?: string;
  width?: number;
  dashed?: boolean;
}



interface IdeaGalaxyProps {
  isPageMode?: boolean;
}

export function IdeaGalaxy({ isPageMode = false }: IdeaGalaxyProps) {
  const [ideas, setIdeas] = useState<Idea[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const graphRef = useRef<ForceGraphMethods>();
  const containerRef = useRef<HTMLDivElement>(null);
  const { isDarkMode } = useDarkMode();
  const [ideaDepth, setIdeaDepth] = useState(1);
  const [dimensions, setDimensions] = useState({ width: window.innerWidth, height: window.innerHeight });

  useEffect(() => {
    const handleResize = () => {
      setDimensions({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const allIdeas = await ideasService.getAllIdeas(500);
      setIdeas(allIdeas);
    } catch (error) {
      console.error('Failed to fetch ideas for galaxy:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const graphData = useMemo(() => {
    const nodes: GraphNode[] = [];
    const links: GraphLink[] = [];
    const seedMap = new Map<string, { node: GraphNode, ideaCount: number }>();

    // First pass: Count ideas per seed
    ideas.forEach(idea => {
      if (!seedMap.has(idea.seedId)) {
        seedMap.set(idea.seedId, {
          node: {
            id: idea.seedId,
            group: 'seed',
            val: 20, // Base size
            name: idea.seedPrompt || 'Unknown Seed',
            category: idea.seedCategory,
            // Premium Galaxy Palette: Bright Cyan/Blue for Seeds
            color: isDarkMode ? '#22d3ee' : '#0891b2' // cyan-400 / cyan-600
          },
          ideaCount: 0
        });
      }
      
      // Increment count based on TOTAL ideas in the submission (ignoring depth)
      // This ensures the node size reflects the true popularity in the database
      const totalIdeasInSubmission = idea.content.length;
      const current = seedMap.get(idea.seedId)!;
      current.ideaCount += totalIdeasInSubmission;
    });

    // Add Seed Nodes with dynamic size
    const seedNodes: GraphNode[] = [];
    let maxIdeaCount = -1;
    let sunId = '';

    seedMap.forEach(({ node, ideaCount }) => {
      // Scale: Logarithmic scale to prevent massive nodes
      // Base 30, max 120
      const size = Math.min(30 + Math.log(ideaCount + 1) * 20, 120);
      const seedNode = { ...node, val: size };
      
      // Identify the Sun (Largest Node)
      if (ideaCount > maxIdeaCount) {
        maxIdeaCount = ideaCount;
        sunId = seedNode.id;
      }

      nodes.push(seedNode);
      seedNodes.push(seedNode);
    });

    // Mark the Sun
    const sunNode = nodes.find(n => n.id === sunId);
    if (sunNode) {
      sunNode.isSun = true;
      // Premium Sun Color: Gold/Orange/Amber
      sunNode.color = isDarkMode ? '#f59e0b' : '#d97706'; // amber-500 / amber-600
      sunNode.val = sunNode.val * 1.5; // Make it significantly bigger
    }

    // Category Connections: Link seeds in the same category
    for (let i = 0; i < seedNodes.length; i++) {
      for (let j = i + 1; j < seedNodes.length; j++) {
        if (seedNodes[i].category === seedNodes[j].category && seedNodes[i].category) {
          links.push({
            source: seedNodes[i].id,
            target: seedNodes[j].id,
            // Visbility Boost: Increased opacity significantly (0.05 -> 0.2/0.15) and width
            color: isDarkMode ? 'rgba(34, 211, 238, 0.2)' : 'rgba(8, 145, 178, 0.15)', 
            width: 2, // Thicker category links
            dashed: true
          });
        }
      }
    }

    // Second pass: Add Idea nodes and links
    const ideaNodes: { id: string, text: string }[] = [];
    
    ideas.forEach(idea => {
      // Add Idea Nodes based on Depth
      const ideasToShow = idea.content.slice(0, ideaDepth);
      
      ideasToShow.forEach((content, index) => {
        const ideaNodeId = `idea-${idea.id}-${index}`;
        nodes.push({
          id: ideaNodeId,
          group: 'idea',
          val: 6, // Smaller, tighter ideas
          name: content || 'Empty Idea',
          // Premium Galaxy Palette: Deep Purple/Pink for Ideas
          color: isDarkMode ? '#c084fc' : '#9333ea' // purple-400 / purple-600
        });
        
        ideaNodes.push({ id: ideaNodeId, text: content });

        // Link Idea to Seed
        links.push({
          source: ideaNodeId,
          target: idea.seedId,
          // Visibility Boost: Clearer hierarchy connections
          color: isDarkMode ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)'
        });
      });
    });

    // Similarity Connections: Link ideas with similar text
    // Simple word overlap logic
    ideaNodes.forEach((nodeA, i) => {
      // Only compare with a subset to avoid O(N^2) performance hit on large datasets
      // For now, we compare with next 50 nodes
      for (let j = i + 1; j < Math.min(i + 50, ideaNodes.length); j++) {
        const nodeB = ideaNodes[j];
        
        // Skip if same seed (already connected via seed) - actually hard to check here without map, 
        // but similarity links between same seed ideas are fine too.

        const wordsA = new Set(nodeA.text.toLowerCase().split(/\s+/).filter(w => w.length > 3));
        const wordsB = new Set(nodeB.text.toLowerCase().split(/\s+/).filter(w => w.length > 3));
        
        let overlap = 0;
        wordsA.forEach(w => {
          if (wordsB.has(w)) overlap++;
        });

        // Threshold for connection
        if (overlap >= 2) {
          const strength = Math.min(overlap, 5); // Max width 5
          links.push({
            source: nodeA.id,
            target: nodeB.id,
            // Visibility Boost: Much clearer similarity links
            color: isDarkMode ? `rgba(244, 114, 182, ${0.15 * strength})` : `rgba(219, 39, 119, ${0.15 * strength})`, 
            width: strength * 0.8, // Slightly thicker
            dashed: false
          });
        }
      }
    });

    return { nodes, links };
  }, [ideas, isDarkMode, ideaDepth]);

  useEffect(() => {
    // Physics tuning based on density and screen size
    if (graphRef.current && graphData?.nodes) {
      const nodeCount = graphData.nodes.length;
      const screenArea = dimensions.width * dimensions.height;
      // Density factor: Higher means more crowded
      const density = nodeCount / screenArea * 10000; 

      // Adaptive Scaling:
      // Calculate a scale factor based on the smaller screen dimension.
      // Reference: 1080px (standard monitor height) -> scale 1.0
      // 400px (mobile) -> scale 0.4
      const minDim = Math.min(dimensions.width, dimensions.height);
      const scaleFactor = Math.max(minDim / 1000, 0.5); // Clamp minimum scale to 0.5

      // Dynamic repulsion: TIGHTER thanks to adaptive scaling
      // Reduced base charge heavily (-400 -> -80) to bring nodes closer
      // UPDATE: Increased to -300 to prevent clumping (Doomsday fix)
      const baseCharge = -300 * scaleFactor; 
      // Ensure charge doesn't drop too low even with density
      const chargeStrength = Math.min(baseCharge - (density * 10 * scaleFactor), -150);

      // Dynamic link distance: ORBITAL LOGIC
      // Instead of a fixed distance, we calculate it based on the size of the nodes.
      // constant + sourceRadius + targetRadius ensures they never overlap and "orbit" outside.
      const getLinkDistance = (link: any) => {
        const sourceVal = link.source.val || 10;
        const targetVal = link.target.val || 10;
        
        // Base padding between nodes
        let padding = 30 * scaleFactor;
        
        // Similarity links (pink) should be longer to avoid pulling diverse clusters together
        if (link.color?.includes('119') || link.color?.includes('182')) {
           padding = 60 * scaleFactor;
        }

        return sourceVal + targetVal + padding;
      };

      // Balanced repulsion
      graphRef.current.d3Force('charge')?.strength(chargeStrength);
      
      // Dynamic link distance with Orbital Logic
      graphRef.current.d3Force('link')?.distance(getLinkDistance);
      
      // --- CUSTOM ORBITAL PHYSICS ---
      // 1. PIN THE SUN: Fix the largest node to the center
      const sun = graphData.nodes.find(n => n.isSun);
      if (sun) {
        sun.fx = 0;
        sun.fy = 0;
      }

      // 2. ORBITAL FORCE: Apply tangential velocity to create rotation
      const orbitalForce = (alpha: number) => {
        const center = { x: 0, y: 0 };
        const rotationSpeed = 0.0003; // SLOWER rotation

        graphData.nodes.forEach((node: any) => {
          if (node.isSun) return; // Sun doesn't orbit
          
          const dx = node.x - center.x;
          const dy = node.y - center.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          if (distance > 10) { // Avoid singularities at center
            // Tangential vector (-y, x)
             node.vx += -(dy / distance) * rotationSpeed;
             node.vy += (dx / distance) * rotationSpeed;
          }
        });
      };
      
      // Inject custom force
      graphRef.current.d3Force('orbit', orbitalForce);

      // 3. GRAVITY WELL: Pull everything towards the center (counteract centrifugal force)
      // Pull to a ring at 35% of screen min-dimension (approx 350-400px)
      // This pushes the orbit OUT away from the sun
      const orbitRadius = minDim * 0.35;
      graphRef.current.d3Force('radial', forceRadial(orbitRadius, 0, 0).strength(0.08));
      
      // Add collision to prevent overlap
      // Increased buffer significantly (1.5 -> 2.5) to prevent "disaster" clumping
      graphRef.current.d3Force('collide')?.strength(0.8).radius((node: any) => node.val * 2.5);
      
      // Initial Zoom Fit
      setTimeout(() => {
        graphRef.current?.zoomToFit(600, 100); 
      }, 500);
    }
  }, [graphData, dimensions]); // Re-run when graph data or dimensions change

  const handleRefresh = () => {
    fetchData();
  };

  const toggleFullscreen = () => {
    if (!containerRef.current) return;
    
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().catch(err => {
        console.error(`Error attempting to enable fullscreen: ${err.message}`);
      });
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Listen for fullscreen change events to update state if user presses Esc
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
      // Recenter graph after resize
      setTimeout(() => graphRef.current?.zoomToFit(400), 100);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);
  if (loading && ideas.length === 0) {
    return (
      <div className={`flex flex-col items-center justify-center ${isPageMode ? 'h-full w-full' : 'min-h-[400px]'} ${isDarkMode ? 'bg-gray-950' : 'bg-gray-50'}`}>
        <Loader2 className="h-8 w-8 animate-spin text-blue-500 mb-4" />
        <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Loading the Idea Galaxy...</p>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`relative transition-all duration-300 ${isDarkMode ? 'bg-gray-950' : 'bg-slate-50'} ${isPageMode ? 'h-full w-full' : ''} ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}>
      {/* Galaxy Background: Deep space gradient + stars */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: isDarkMode 
            ? 'radial-gradient(circle at center, #1e1b4b 0%, #020617 100%)' 
            : 'radial-gradient(circle at center, #f0f9ff 0%, #f8fafc 100%)',
        }}
      >
        {/* Subtle Starscape (CSS generated dots) */}
        <div 
          className="absolute inset-0"
          style={{
             backgroundImage: `radial-gradient(${isDarkMode ? '#ffffff' : '#94a3b8'} 1.5px, transparent 1.5px), radial-gradient(${isDarkMode ? '#ffffff' : '#94a3b8'} 1px, transparent 1px)`,
             backgroundSize: '50px 50px, 20px 20px',
             backgroundPosition: '0 0, 25px 25px',
             opacity: isDarkMode ? 0.15 : 0.1
          }}
        />
      </div>
      
      <div className={`relative overflow-hidden ${isPageMode || isFullscreen ? 'h-full w-full' : 'h-[600px] rounded-xl border border-gray-200 dark:border-gray-800'}`}>
        <div className="absolute top-6 right-6 z-10 flex gap-2 items-center">
          {/* Idea Depth Control */}
          <div className="mr-4 flex items-center gap-3 bg-white/10 dark:bg-black/20 backdrop-blur-md px-4 py-2 rounded-full border border-white/20 dark:border-white/10 shadow-xl">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-200">
              <Layers className="h-4 w-4" />
              <span>Depth: {ideaDepth}</span>
            </div>
            <div className="w-32">
              <Slider
                value={[ideaDepth]}
                onValueChange={(vals) => setIdeaDepth(vals[0])}
                min={1}
                max={10}
                step={1}
                className="cursor-pointer"
              />
            </div>
          </div>

          <button
            onClick={handleRefresh}
            className="p-2 bg-white/10 dark:bg-black/20 backdrop-blur-md rounded-full shadow-lg hover:bg-white/20 dark:hover:bg-white/10 transition-all border border-white/20 dark:border-white/10 group"
            title="Refresh Galaxy"
          >
            <RefreshCw className={`h-5 w-5 text-gray-600 dark:text-gray-300 group-hover:text-blue-500 dark:group-hover:text-white transition-colors ${loading ? 'animate-spin' : ''}`} />
          </button>
          {!isPageMode && (
            <button
              onClick={toggleFullscreen}
              className="p-2 bg-white/10 dark:bg-black/20 backdrop-blur-md rounded-full shadow-lg hover:bg-white/20 dark:hover:bg-white/10 transition-all border border-white/20 dark:border-white/10 group"
              title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
            >
              {isFullscreen ? (
                <Minimize2 className="h-5 w-5 text-gray-600 dark:text-gray-300 group-hover:text-blue-500 dark:group-hover:text-white transition-colors" />
              ) : (
                <Maximize2 className="h-5 w-5 text-gray-600 dark:text-gray-300 group-hover:text-blue-500 dark:group-hover:text-white transition-colors" />
              )}
            </button>
          )}
        </div>

        <div className="absolute top-6 left-6 z-10 pointer-events-none">
          <h2 className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 dark:from-cyan-400 dark:to-purple-400 drop-shadow-sm tracking-tight font-display">
            Galaxy View
          </h2>
          <div className="flex items-center gap-3 mt-2">
            <span className="px-2 py-1 rounded-full bg-cyan-100/50 dark:bg-cyan-500/10 border border-cyan-200 dark:border-cyan-500/20 text-cyan-700 dark:text-cyan-300 text-xs font-bold uppercase tracking-wider backdrop-blur-sm">
              {graphData.nodes.filter(n => n.group === 'seed').length} Stars
            </span>
            <span className="px-2 py-1 rounded-full bg-purple-100/50 dark:bg-purple-500/10 border border-purple-200 dark:border-purple-500/20 text-purple-700 dark:text-purple-300 text-xs font-bold uppercase tracking-wider backdrop-blur-sm">
              {graphData.nodes.filter(n => n.group === 'idea').length} Planets
            </span>
          </div>
        </div>

        <ForceGraph2D
          ref={graphRef}
          width={isPageMode || isFullscreen ? window.innerWidth : undefined}
          height={isPageMode || isFullscreen ? window.innerHeight : 600}
          graphData={graphData}
          nodeLabel="name"
          nodeColor="color"
          nodeRelSize={6}
          linkColor={link => (link as GraphLink).color || (isDarkMode ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)')}
          linkWidth={link => (link as GraphLink).width || 1}
          linkDirectionalParticles={link => (link as GraphLink).color?.includes('119') || (link as GraphLink).color?.includes('182') ? 2 : 0} // Particles on pink similarity links
          linkDirectionalParticleSpeed={0.005}
          linkLineDash={link => (link as GraphLink).dashed ? [4, 2] : null}
          backgroundColor="rgba(0,0,0,0)" // Transparent, handled by parent div for gradient
          d3VelocityDecay={0.06} // INCREASED friction (was 0.02) to stop runaway speed
          d3AlphaDecay={0} // Perpetual Motion
          onNodeDragEnd={node => {
            node.fx = node.x;
            node.fy = node.y;
          }}
          onNodeClick={node => {
            // Zoom to node on click
            graphRef.current?.centerAt(node.x, node.y, 1000);
            graphRef.current?.zoom(4, 2000); // Less aggressive zoom
          }}
          // cooldownTicks={Infinity} // Disabled: Infinite simulation
          onEngineStop={() => {}} // Never stops
          nodeCanvasObject={(node, ctx, globalScale) => {
            // Safety check: coordinates can be NaN/undefined during initialization or instability
            if (!Number.isFinite(node.x) || !Number.isFinite(node.y)) return;

            const label = node.name;
            const fontSize = 14/globalScale; 
            
            // --- Draw Glows (Stars) ---
            if (node.group === 'seed') {
               const isSun = node.isSun;
               const glowRadius = isSun ? node.val * 6 : node.val * 3; // Huge glow for Sun
               
               // Create radial gradient for glow
               const gradient = ctx.createRadialGradient(node.x!, node.y!, node.val, node.x!, node.y!, glowRadius);
               
               if (isSun) {
                  // SUN GLOW: Gold/Orange/Red fiery effect
                  gradient.addColorStop(0, 'rgba(251, 191, 36, 0.8)'); // Amber
                  gradient.addColorStop(0.4, 'rgba(245, 158, 11, 0.4)'); // Orange
                  gradient.addColorStop(1, 'rgba(0,0,0,0)');
               } else {
                  // STAR GLOW: Cyan/Blue
                  gradient.addColorStop(0, node.color || 'white');
                  gradient.addColorStop(1, 'rgba(0,0,0,0)');
               }
               
               ctx.fillStyle = gradient;
               ctx.beginPath();
               ctx.arc(node.x!, node.y!, glowRadius, 0, 2 * Math.PI, false);
               ctx.globalAlpha = isSun ? 0.4 : 0.2; // Stronger glow for sun
               ctx.fill();
               ctx.globalAlpha = 1.0; // Reset alpha
            }

            // --- Draw Node Body ---
            ctx.beginPath();
            ctx.arc(node.x!, node.y!, node.val, 0, 2 * Math.PI, false);
            ctx.fillStyle = node.color || '#fff';
            
            // Slight 3D effect for ideas
            if (node.group === 'idea') {
               ctx.shadowBlur = 5;
               ctx.shadowColor = 'rgba(0,0,0,0.5)';
            }
            
            // Sun Body Effect
            if (node.isSun) {
               ctx.shadowBlur = 50; 
               ctx.shadowColor = '#f59e0b'; // Amber-500
            }

            ctx.fill();
            ctx.shadowBlur = 0; // Reset shadow

            // --- Draw Labels (LOD logic) ---
            const isSeed = node.group === 'seed';
            // Show seed labels if they are somewhat visible (scale > 0.5)
            // Show idea labels ONLY if zoomed in (scale > 2.0) or hovered (handled by library usually, but here we hardcode zoom)
            const shouldShowLabel = isSeed ? globalScale > 0.4 : globalScale > 2.2;

            if (shouldShowLabel) {
              ctx.font = `600 ${fontSize}px Inter, system-ui, sans-serif`;
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              
              // Variable opacity for smooth fade in
              let opacity = 1;
              if (isSeed) {
                 opacity = Math.min((globalScale - 0.4) * 2, 1);
              } else {
                 opacity = Math.min((globalScale - 2.2) * 2, 1);
              }
              
              ctx.globalAlpha = opacity;

              const textWidth = ctx.measureText(label).width;
              const padding = fontSize * 0.6;
              const bgHeight = fontSize + padding;
              const bgWidth = textWidth + padding * 2;
              const bgY = node.y! + node.val + fontSize * 0.8;
              
              // Label Background
              ctx.fillStyle = isDarkMode ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.7)';
              // Draw rounded rect manually or use roundRect if supported (recent browsers)
              if (ctx.roundRect) {
                  ctx.beginPath();
                  ctx.roundRect(node.x! - bgWidth/2, bgY - bgHeight/2, bgWidth, bgHeight, 6);
                  ctx.fill();
              } else {
                  ctx.fillRect(node.x! - bgWidth/2, bgY - bgHeight/2, bgWidth, bgHeight);
              }

              // Label Text
              ctx.fillStyle = isDarkMode ? '#e2e8f0' : '#0f172a';
              ctx.fillText(label, node.x!, bgY);
              
              ctx.globalAlpha = 1.0; // Reset
            }
          }}
        />
      </div>
    </div>
  );
}
